/**
 * 文件简介：
 * - ECharts 图表生命周期计数器，用于验证「当前页 init 一次 + 定时 setOption 更新」是否生效
 * - 挂载到 window.__chartLifecycleStats，便于 Chrome 控制台随时查看
 * 使用方法：
 * - __chartLifecycleStats.summary()           打印图表汇总
 * - __screenLifecycleStats.summary()          打印全屏汇总（含图表 + 组件/定时器/WS/数据层）
 * - 建议测试前同时 reset：__chartLifecycleStats.reset(); __screenLifecycleStats.reset();
 * - __chartLifecycleStats.reset()             清零计数（刷新页面前对比用）
 * - __chartLifecycleStats.getStats()          返回原始计数对象
 * 原理：
 * - 仅在 echarts.init / dispose / 复用 / setOption 等关键路径打点，不在每次 tick 打 info 日志
 * - echarts.init 会打 info 日志（需 VUE_APP_DEBUG 或 localStorage yuanqi_debug=1）
 */

import logger from '@/utils/logger';

/** 最近事件环形缓冲上限 */
const MAX_RECENT_EVENTS = 50;

function createEmptyStats() {
  return {
    startedAt: Date.now(),
    /** initCharts() 被调用次数（轮播 finish / mounted 等，不等于 echarts.init） */
    initChartsCalls: 0,
    /** 真正 echarts.init 次数（核心指标：不应随 6s 定时器线性增长） */
    echartsInit: 0,
    echartsInitByReason: {
      'initCharts-new': 0,
      'initChart-legacy': 0,
      'updateChart-fallback': 0,
    },
    /** DOM 上已有实例，getInstanceByDom 绑定（非 init） */
    bindExistingDom: 0,
    /** 实例已存在且 DOM 匹配，只走 updateChart */
    reuseUpdate: 0,
    /** updateChart 内 setOption(series) 次数 */
    setOptionUpdates: 0,
    dispose: 0,
    disposeByReason: {
      'off-page': 0,
      'orphan-dom': 0,
      'dom-mismatch': 0,
      'dispose-all': 0,
    },
    /** 按 roomKey 聚合：init/dispose/reuse/setOption */
    perRoomKey: {},
    recentEvents: [],
  };
}

let stats = createEmptyStats();

function ensureRoomStats(roomKey) {
  if (!stats.perRoomKey[roomKey]) {
    stats.perRoomKey[roomKey] = {
      init: 0,
      dispose: 0,
      reuse: 0,
      setOption: 0,
      lastInitAt: null,
      lastInitReason: null,
    };
  }
  return stats.perRoomKey[roomKey];
}

function pushRecentEvent(type, detail) {
  stats.recentEvents.push({ at: Date.now(), type, ...detail });
  if (stats.recentEvents.length > MAX_RECENT_EVENTS) {
    stats.recentEvents.shift();
  }
}

function countWaveDataPoints(appointmentData) {
  if (!Array.isArray(appointmentData)) {
    return { appointmentDataLength: 0, waveDataTotalPoints: 0 };
  }
  let waveDataTotalPoints = 0;
  appointmentData.forEach((item) => {
    if (!item || !item.waveData) return;
    ['delta', 'theta', 'alpha', 'beta', 'gamma'].forEach((band) => {
      const arr = item.waveData[band];
      if (Array.isArray(arr)) {
        waveDataTotalPoints += arr.length;
      }
    });
  });
  return {
    appointmentDataLength: appointmentData.length,
    waveDataTotalPoints,
  };
}

export const chartLifecycleStats = {
  recordInitChartsCall() {
    stats.initChartsCalls += 1;
    pushRecentEvent('initCharts.call', {});
  },

  /**
   * 记录一次 echarts.init（真正创建 Canvas 实例）
   * @param {string} reason initCharts-new | initChart-legacy | updateChart-fallback
   */
  recordEchartsInit(reason, roomKey, meta = {}) {
    stats.echartsInit += 1;
    if (stats.echartsInitByReason[reason] == null) {
      stats.echartsInitByReason[reason] = 0;
    }
    stats.echartsInitByReason[reason] += 1;

    const room = ensureRoomStats(roomKey);
    room.init += 1;
    room.lastInitAt = Date.now();
    room.lastInitReason = reason;

    pushRecentEvent('echarts.init', { reason, roomKey, ...meta });
    logger.info('[ChartLifecycle] echarts.init', {
      reason,
      roomKey,
      totalInit: stats.echartsInit,
      ...meta,
    });
  },

  recordBindExistingDom(roomKey, meta = {}) {
    stats.bindExistingDom += 1;
    pushRecentEvent('bind-existing-dom', { roomKey, ...meta });
    logger.debug('[ChartLifecycle] bind existing dom (非 init)', { roomKey, ...meta });
  },

  recordReuse(roomKey, meta = {}) {
    stats.reuseUpdate += 1;
    ensureRoomStats(roomKey).reuse += 1;
    pushRecentEvent('reuse', { roomKey, ...meta });
    logger.debug('[ChartLifecycle] reuse instance', { roomKey, ...meta });
  },

  recordSetOption(roomKey) {
    stats.setOptionUpdates += 1;
    ensureRoomStats(roomKey).setOption += 1;
  },

  /**
   * 记录 dispose
   * @param {string} reason off-page | orphan-dom | dom-mismatch | dispose-all
   */
  recordDispose(reason, roomKey) {
    stats.dispose += 1;
    if (stats.disposeByReason[reason] == null) {
      stats.disposeByReason[reason] = 0;
    }
    stats.disposeByReason[reason] += 1;
    if (roomKey) {
      ensureRoomStats(roomKey).dispose += 1;
    }
    pushRecentEvent('dispose', { reason, roomKey });
    logger.debug('[ChartLifecycle] dispose', {
      reason,
      roomKey,
      totalDispose: stats.dispose,
    });
  },

  /**
   * 控制台汇总：判断 init 是否异常（setOptionPerInitRatio 应远大于 1）
   * @param {object} context 可选：{ appointmentData, activeChartInstances }
   */
  summary(context = {}) {
    const elapsedMs = Date.now() - stats.startedAt;
    const waveStats = countWaveDataPoints(context.appointmentData);
    const setOptionPerInitRatio = stats.echartsInit > 0
      ? Number((stats.setOptionUpdates / stats.echartsInit).toFixed(1))
      : null;

    const result = {
      elapsedMinutes: Number((elapsedMs / 60000).toFixed(1)),
      initChartsCalls: stats.initChartsCalls,
      echartsInit: stats.echartsInit,
      echartsInitByReason: { ...stats.echartsInitByReason },
      bindExistingDom: stats.bindExistingDom,
      reuseUpdate: stats.reuseUpdate,
      setOptionUpdates: stats.setOptionUpdates,
      /** setOption 次数 / init 次数；正常应随运行时间增大（定时更新远多于 init） */
      setOptionPerInitRatio,
      dispose: stats.dispose,
      disposeByReason: { ...stats.disposeByReason },
      activeChartInstances: context.activeChartInstances ?? null,
      ...waveStats,
    };

    // eslint-disable-next-line no-console
    console.info('[ChartLifecycle] summary', result);
    // eslint-disable-next-line no-console
    console.info('[ChartLifecycle] perRoomKey', { ...stats.perRoomKey });
    // eslint-disable-next-line no-console
    console.info('[ChartLifecycle] recentEvents (last %d)', stats.recentEvents.length, [...stats.recentEvents]);

    return result;
  },

  reset() {
    stats = createEmptyStats();
    // eslint-disable-next-line no-console
    console.info('[ChartLifecycle] counters reset');
    return stats;
  },

  getStats() {
    return stats;
  },
};

if (typeof window !== 'undefined') {
  window.__chartLifecycleStats = chartLifecycleStats;
}

export default chartLifecycleStats;
